import { useState } from "react"
import { Helmet } from "react-helmet"

type FaqItem = {
  question: string
  answer: string
}

const faqs: FaqItem[] = [
  {
    question: "What are event marketing posters?",
    answer:
      "An event marketing poster is a personalized, shareable graphic that attendees post on social media to promote your event.",
  },
  {
    question: "How do event marketing posters increase attendance?",
    answer:
      "They turn attendees into promoters. When someone shares a personalized poster, their network sees authentic social proof from someone they trust. Our customers average 4x more registrations.",
  },
  {
    question: "What's the best way to promote an event on social media?",
    answer:
      "Combine paid ads with organic sharing. Give attendees personalized posters they actually want to post—unlike generic flyers, these feel authentic and drive higher engagement.",
  },
  {
    question: "How can I get attendees to share my event?",
    answer:
      "Make it effortless. Personalized posters make them look good, pre-written captions save time, and one-tap sharing takes 10 seconds. Easy sharing = 10x more participation.",
  },
  {
    question: "Can I customize event posters to match my brand?",
    answer:
      "Yes. Full control over fonts, colors, layouts, and visuals. Enterprise customers can request custom designs for an additional charge.",
  },
  {
    question: "Do I have to create separate posters for each attendee?",
    answer:
      "No. Create the template once, then share a link or embed our widget on your registration page. Attendees personalize it themselves—manually or via LinkedIn auto-fill. Takes them seconds, zero work for you.",
  },
  {
    question:
      "Will attendees have to upload their photo and details to the poster?",
    answer:
      "Their choice. They can manually add details and upload a photo, or sign in with LinkedIn for instant auto-fill of their name, photo, and job title.",
  },
  {
    question:
      "Is my attendee data secure? How do you handle LinkedIn information?",
    answer:
      "Completely secure. We use LinkedIn’s official API with explicit user permission. We only access name, photo, and title—never share data with third parties. GDPR and CCPA compliant.",
  },
  {
    question: "Can we integrate the posters into our event registration page?",
    answer:
      "Yes. Our poster widget embeds directly into your registration flow so attendees can create their personalized poster right after signing up.",
  },
  {
    question: "Can we run poster campaigns via Premagic?",
    answer:
      "Yes. We offer email and WhatsApp campaigns as a chargeable add-on. Each campaign includes a preview of the attendee’s personalized poster—much higher conversion than generic emails.",
  },
  {
    question:
      "Which social media platforms can attendees share their event posters on?",
    answer:
      "Instagram, LinkedIn, WhatsApp, Email, Facebook, X, WeChat, KakaoTalk, Snapchat, Telegram, and VKontakte. Mobile-optimized for every platform with one-tap sharing.",
  },
  {
    question: "How is this different from other event poster tools?",
    answer:
      "Traditional tools create generic, desktop-sized posters. Ours are personalized per attendee, mobile-optimized, include tracking links, and come with pre-written captions. Result: 10x more shares.",
  },
  {
    question: "How do I track which attendees are promoting my event?",
    answer:
      "Real-time leaderboard shows who’s sharing, clicking, and converting. Each poster has a unique tracking link so you can measure individual impact and reward top promoters. Download detailed reports anytime.",
  },
  {
    question: "Does this work for virtual events, in-person events, or both?",
    answer:
      "All formats—virtual, in-person, and hybrid. The strategy is the same: personalized content that attendees want to share = multiplied organic reach.",
  },
]

export function FaqAccordionSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const toggle = (index: number) => {
    setOpenIndex(current => (current === index ? null : index))
  }

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(item => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
              }
            }))
          })}
        </script>
      </Helmet>
      <section className="bg-[#fff7f4] py-16 px-4 md:px-0">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-center text-2xl md:text-3xl font-bold text-[#222222] mb-10">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">
            {faqs.map((item, index) => {
              const isOpen = openIndex === index

              return (
                <div
                  key={item.question}
                  className="rounded-2xl bg-[#ffe7dd] shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => toggle(index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left"
                  >
                    <span className="font-semibold text-[15px] md:text-base text-[#2a2530]">
                      {item.question}
                    </span>
                    <span className="h-4 w-4 p-2 ml-4 flex items-center justify-center rounded-full bg-white text-sm font-bold text-[#51414b]">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>

                  <div
                    className={`
    grid transition-all duration-300 ease-in-out
    ${isOpen ? "grid-rows-[1fr] opacity-100 py-2" : "grid-rows-[0fr] opacity-0"}
    border-t border-[#f4cfc1]
  `}
                  >
                    <div className="overflow-hidden px-5  text-sm md:text-[15px] text-[#4d4048]">
                      {item.answer}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>
    </>
  )
}
